"use client";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { customInstance } from "@/lib/api/client";

export interface PlanResponse {
  id: string;
  slug: string;
  name: string;
  is_custom: boolean;
  max_seats: number | null;
  price_monthly_cents: number | null;
  price_annual_cents: number | null;
  credits_per_seat: number;
  mailboxes_per_seat: number;
  linkedin_per_seat: number;
  features: Record<string, boolean>;
}

export interface SubscriptionAddonRef {
  id: string;
  slug: string;
  name: string;
  status: string;
}

export interface SubscriptionResponse {
  id: string;
  plan: PlanResponse;
  status: string;
  billing_cadence: string;
  seat_count: number;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at: string | null;
  resume_at: string | null;
  managed_externally: boolean;
  addons: SubscriptionAddonRef[];
  pending_downgrade_plan: PlanResponse | null;
  pending_downgrade_cadence: string | null;
  pending_downgrade_seat_count: number | null;
  pending_downgrade_at: string | null;
  discount_percent: number | null;
  discount_period_end: string | null;
}

export interface CreditBalanceResponse {
  org_id: string;
  balance: number;
  credits_per_seat: number;
  seat_count: number;
  total_allocation: number;
  next_cycle_allocation: number;
  period_start: string | null;
  period_end: string | null;
}

export interface SeatInfo {
  seat_count: number;
  active_seats: number;
  pending_invitations: number;
  available_seats: number;
}

export interface SeatAssignment {
  id: string;
  org_id: string;
  user_id: string;
  user_name: string | null;
  user_email: string | null;
  role: string | null;
  status: string;
  assigned_at: string;
  deactivated_at: string | null;
}

export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

export interface InvoiceLineItem {
  description: string | null;
  amount: number | null;
  quantity: number | null;
}

export interface Invoice {
  id: string;
  number: string | null;
  amount_due: number;
  amount_paid: number;
  total: number | null;
  subtotal: number | null;
  tax: number | null;
  currency: string | null;
  status: string;
  billing_reason: string | null;
  description: string | null;
  period_start: number;
  period_end: number;
  invoice_pdf: string | null;
  hosted_invoice_url: string | null;
  receipt_url: string | null;
  created: number;
  line_items: InvoiceLineItem[];
}

export interface Addon {
  id: string;
  slug: string;
  name: string;
  price_monthly_cents: number;
  price_annual_cents: number;
  scope: "org" | "seat";
  eligible_plan_slugs: string[];
  is_subscribed: boolean;
  subscription_addon_id: string | null;
}

export interface BillingDetails {
  email: string | null;
  name: string | null;
  company_name: string | null;
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
}

export function useSubscription() {
  return useQuery({
    queryKey: ["billing", "subscription"],
    queryFn: () =>
      customInstance<SubscriptionResponse | null>("/api/billing/subscription", {
        method: "GET",
      }),
  });
}

export function useCreditBalance() {
  return useQuery({
    queryKey: ["billing", "credits"],
    queryFn: () =>
      customInstance<CreditBalanceResponse>("/api/billing/credits", {
        method: "GET",
      }),
    staleTime: 30_000,
  });
}

export function usePlans() {
  return useQuery({
    queryKey: ["billing", "plans"],
    queryFn: () =>
      customInstance<PlanResponse[]>("/api/billing/plans", {
        method: "GET",
      }),
  });
}

export interface OrgStatusResponse {
  suspended: boolean;
  suspension_status: string | null;
  suspension_reason: string | null;
  has_subscription: boolean;
  subscription_status: string | null;
  plan_slug: string | null;
  has_open_invoices: boolean;
}

export function useOrgStatus() {
  return useQuery({
    queryKey: ["billing", "org-status"],
    queryFn: () =>
      customInstance<OrgStatusResponse>("/api/billing/org-status", {
        method: "GET",
      }),
  });
}

export function useSeatInfo() {
  return useQuery({
    queryKey: ["billing", "seats"],
    queryFn: () =>
      customInstance<SeatInfo>("/api/billing/seats", { method: "GET" }),
  });
}

export function useSeatAssignments() {
  return useQuery({
    queryKey: ["billing", "seats", "assignments"],
    queryFn: () =>
      customInstance<SeatAssignment[]>("/api/billing/seats/assignments", {
        method: "GET",
      }),
  });
}

export function usePaymentMethod() {
  return useQuery({
    queryKey: ["billing", "payment-method"],
    queryFn: () =>
      customInstance<PaymentMethod | null>("/api/billing/payment-method", {
        method: "GET",
      }),
  });
}

export function useSavedPaymentMethods() {
  return useQuery({
    queryKey: ["billing", "payment-methods"],
    queryFn: () =>
      customInstance<PaymentMethod[]>("/api/billing/payment-methods", {
        method: "GET",
      }),
  });
}

export function useDeletePaymentMethod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pmId: string) =>
      customInstance(`/api/billing/payment-methods/${pmId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing", "payment-methods"] });
    },
  });
}

export interface AppliedCoupon {
  code: string;
  promotion_code_id: string;
  name: string | null;
  percent_off: number | null;
  amount_off_cents: number | null;
  currency: string | null;
  duration: "once" | "repeating" | "forever";
  duration_in_months: number | null;
  restricted_plan_slug: string | null;
  restricted_cadence: string | null;
}

export function useValidateCoupon() {
  return useMutation({
    mutationFn: ({
      code,
      planSlug,
      billingCadence,
    }: {
      code: string;
      planSlug?: string;
      billingCadence?: string;
    }) =>
      customInstance<AppliedCoupon>("/api/billing/validate-coupon", {
        method: "POST",
        body: JSON.stringify({
          code,
          plan_slug: planSlug ?? null,
          billing_cadence: billingCadence ?? null,
        }),
        headers: { "Content-Type": "application/json" },
      }),
  });
}

export function useInvoices() {
  return useQuery({
    queryKey: ["billing", "invoices"],
    queryFn: () =>
      customInstance<Invoice[]>("/api/billing/invoices", { method: "GET" }),
  });
}

// ------------------------------------------------------------------
// Cancel flow (churn prevention)
// ------------------------------------------------------------------

const JSON_HEADERS = { "Content-Type": "application/json" } as const;

export interface CancelReasonOption {
  code: string;
  label: string;
}

export interface CancelContext {
  eligible: boolean;
  reason_ineligible: string | null;
  plan_slug: string | null;
  plan_name: string | null;
  cadence: "monthly" | "annual" | null;
  period_end: string | null;
  can_pause: boolean;
  pause_months_options: number[];
  discount_available: boolean;
  discount_percent: number | null;
  discount_redeemed: boolean;
  reasons: CancelReasonOption[];
}

export function useCancelContext(enabled: boolean) {
  return useQuery({
    queryKey: ["billing", "cancel-context"],
    queryFn: () =>
      customInstance<CancelContext>("/api/billing/cancel/context", {
        method: "GET",
      }),
    enabled,
    staleTime: 0,
    refetchOnMount: true,
  });
}

export function useCancelPause() {
  return useMutation({
    mutationFn: (months: number) =>
      customInstance<{ resume_at: string; pause_start: string | null }>(
        "/api/billing/cancel/pause",
        {
          method: "POST",
          body: JSON.stringify({ months }),
          headers: JSON_HEADERS,
        },
      ),
  });
}

export function useCancelFeedback() {
  return useMutation({
    mutationFn: (vars: { reason_code: string; reason_text?: string | null }) =>
      customInstance<{
        discount_available: boolean;
        discount_percent: number | null;
      }>("/api/billing/cancel/feedback", {
        method: "POST",
        body: JSON.stringify({
          reason_code: vars.reason_code,
          reason_text: vars.reason_text ?? null,
        }),
        headers: JSON_HEADERS,
      }),
  });
}

export function useCancelDiscount() {
  return useMutation({
    mutationFn: () =>
      customInstance<{
        applied: boolean;
        percent: number;
        period_end: string | null;
      }>("/api/billing/cancel/discount", { method: "POST" }),
  });
}

export function useCancelContact() {
  return useMutation({
    mutationFn: (vars: {
      reason_code?: string | null;
      message?: string | null;
    }) =>
      customInstance<{ submitted: boolean }>("/api/billing/cancel/contact", {
        method: "POST",
        body: JSON.stringify({
          reason_code: vars.reason_code ?? null,
          message: vars.message ?? null,
        }),
        headers: JSON_HEADERS,
      }),
  });
}

export function useConfirmCancel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars?: {
      reason_code?: string | null;
      reason_text?: string | null;
    }) =>
      customInstance<{ cancel_at: string | null }>(
        "/api/billing/cancel/confirm",
        {
          method: "POST",
          body: JSON.stringify({
            reason_code: vars?.reason_code ?? null,
            reason_text: vars?.reason_text ?? null,
          }),
          headers: JSON_HEADERS,
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing", "subscription"] });
      queryClient.invalidateQueries({ queryKey: ["billing", "org-status"] });
    },
  });
}

/** Undo a scheduled (future) cancellation — keeps the subscription active. */
export function useUndoCancellation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      customInstance<{ status: string; cancel_at: string | null }>(
        "/api/billing/cancel/undo",
        { method: "POST" },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing"] });
    },
  });
}

/** Cancel a pending plan downgrade — keep the current plan. */
export function useCancelDowngrade() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      customInstance<{ cancelled: boolean }>("/api/billing/cancel-downgrade", {
        method: "POST",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing"] });
    },
  });
}

/** Stop a pause before/while it's in effect — keeps the subscription active. */
export function useResumeSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      customInstance<{ resumed: boolean }>("/api/billing/resume", {
        method: "POST",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing"] });
    },
  });
}

export function useActivateSeat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      customInstance("/api/billing/seats/activate", {
        method: "POST",
        body: JSON.stringify({ user_id: userId }),
        headers: { "Content-Type": "application/json" },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing", "seats"] });
      queryClient.invalidateQueries({ queryKey: ["billing", "subscription"] });
    },
  });
}

export function useDeactivateSeat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      customInstance("/api/billing/seats/deactivate", {
        method: "POST",
        body: JSON.stringify({ user_id: userId }),
        headers: { "Content-Type": "application/json" },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing", "seats"] });
      queryClient.invalidateQueries({ queryKey: ["billing", "subscription"] });
    },
  });
}

export function useAddSeat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (count: number) =>
      customInstance("/api/billing/add-seat", {
        method: "POST",
        body: JSON.stringify({ count }),
        headers: { "Content-Type": "application/json" },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing", "seats"] });
      queryClient.invalidateQueries({ queryKey: ["billing", "subscription"] });
    },
  });
}

export function useRemoveSeat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (count: number) =>
      customInstance("/api/billing/remove-seat", {
        method: "POST",
        body: JSON.stringify({ count }),
        headers: { "Content-Type": "application/json" },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing", "seats"] });
      queryClient.invalidateQueries({ queryKey: ["billing", "subscription"] });
    },
  });
}

// ------------------------------------------------------------------
// Add-ons
// ------------------------------------------------------------------

export function useAddons() {
  return useQuery({
    queryKey: ["billing", "addons"],
    queryFn: () =>
      customInstance<Addon[]>("/api/billing/addons", { method: "GET" }),
  });
}

export function useSubscribeAddon() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) =>
      customInstance<{ slug: string; status: string }>(
        "/api/billing/addons/subscribe",
        {
          method: "POST",
          body: JSON.stringify({ slug }),
          headers: JSON_HEADERS,
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing", "addons"] });
      queryClient.invalidateQueries({ queryKey: ["billing", "subscription"] });
      queryClient.invalidateQueries({ queryKey: ["billing", "invoices"] });
    },
  });
}

export function useCancelAddon() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) =>
      customInstance<{ slug: string; status: string }>(
        "/api/billing/addons/cancel",
        {
          method: "POST",
          body: JSON.stringify({ slug }),
          headers: JSON_HEADERS,
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing", "addons"] });
      queryClient.invalidateQueries({ queryKey: ["billing", "subscription"] });
      queryClient.invalidateQueries({ queryKey: ["billing", "invoices"] });
    },
  });
}

// ------------------------------------------------------------------
// Billing change preview + cadence
// ------------------------------------------------------------------

export interface BillingChangePreview {
  currency: string;
  current_plan_name: string;
  plan_name: string;
  current_cadence: "monthly" | "annual";
  cadence: "monthly" | "annual";
  current_seats: number;
  seat_count: number;
  current_recurring_cents: number;
  new_recurring_cents: number;
  renewal_at: string | null;
  amount_due_today_cents: number;
  current_credits_per_cycle: number;
  new_credits_per_cycle: number;
  prorated_credit_delta: number;
}

export interface BillingChangePreviewRequest {
  action: "cadence" | "seats" | "plan";
  cadence?: "monthly" | "annual";
  seat_count?: number;
  plan_slug?: "solo" | "growth" | "business";
}

export function usePreviewBillingChange() {
  return useMutation({
    mutationFn: (body: BillingChangePreviewRequest) =>
      customInstance<BillingChangePreview>("/api/billing/preview-change", {
        method: "POST",
        body: JSON.stringify(body),
        headers: JSON_HEADERS,
      }),
  });
}

export function useChangeCadence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (cadence: "monthly" | "annual") =>
      customInstance<{ action: string; cadence: string }>(
        "/api/billing/change-cadence",
        {
          method: "POST",
          body: JSON.stringify({ cadence }),
          headers: JSON_HEADERS,
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing", "subscription"] });
      queryClient.invalidateQueries({ queryKey: ["billing", "credits"] });
      queryClient.invalidateQueries({ queryKey: ["billing", "invoices"] });
    },
  });
}

// ------------------------------------------------------------------
// Billing details
// ------------------------------------------------------------------

export function useBillingDetails() {
  return useQuery({
    queryKey: ["billing", "details"],
    queryFn: () =>
      customInstance<BillingDetails>("/api/billing/details", { method: "GET" }),
  });
}

export function useUpdateBillingDetails() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Omit<BillingDetails, "company_name">>) =>
      customInstance<BillingDetails>("/api/billing/details", {
        method: "PUT",
        body: JSON.stringify(body),
        headers: JSON_HEADERS,
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(["billing", "details"], data);
      queryClient.invalidateQueries({ queryKey: ["billing", "details"] });
    },
  });
}
